/**
 * The company behind the product: who operates it, what it is wired to, the tools they reach for,
 * and the commands that still run from a terminal. The systems list never carries a secret, only
 * whether one is set.
 */

import { useQuery } from '@tanstack/react-query';

import { request } from '../api/client';
import type { Company as CompanyShape } from '../api/types';
import { moment, since } from '../components/format';
import { Failed, Loading, PageHead, Panel, Pill } from '../components/ui';

export function Company() {
  const { data, isPending, error } = useQuery({
    queryKey: ['company'],
    queryFn: () => request<CompanyShape>('/api/v1/admin/company'),
  });
  if (isPending) return <Loading />;
  if (error) return <Failed error={error} fallback="Could not load the company page." />;

  const wired = data.systems.filter((system) => system.configured).length;
  return (
    <>
      <PageHead
        eyebrow={`Company · ${data.environment}`}
        title="How Electra is run"
        note={`${wired} of ${data.systems.length} systems wired. Read at ${moment(data.generated_at)}.`}
      />

      {data.notes.map((note) => (
        <div key={note} className="notice">
          {note}
        </div>
      ))}

      <div className="panel-grid" style={{ marginBottom: 22 }}>
        <Panel eyebrow="Operators" title="Who has god mode" flush>
          <table>
            <thead>
              <tr>
                <th>Person</th>
                <th>Signed in</th>
                <th>Last seen</th>
              </tr>
            </thead>
            <tbody>
              {data.operators.map((operator) => (
                <tr key={operator.email}>
                  <td>
                    {operator.full_name ?? <span className="faint">never signed in</span>}
                    <div className="muted small">{operator.email}</div>
                  </td>
                  <td>
                    <Pill value={operator.user_id ? 'active' : 'pending'} />
                  </td>
                  <td className="muted">{since(operator.last_seen_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="panel-body muted small">
            The list is <code>PLATFORM_ADMIN_EMAILS</code> on the API. Adding somebody is a deploy,
            on purpose: nobody can grant god mode from inside it.
          </div>
        </Panel>

        <Panel eyebrow="Systems" title="What the product depends on" flush>
          <table>
            <thead>
              <tr>
                <th>System</th>
                <th>Role</th>
                <th>State</th>
              </tr>
            </thead>
            <tbody>
              {data.systems.map((system) => (
                <tr key={system.name}>
                  <td>
                    {system.name}
                    <div className="muted small">{system.detail}</div>
                  </td>
                  <td className="muted">{system.role}</td>
                  <td>
                    <Pill value={system.configured ? 'ok' : 'closed'} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      </div>

      <div className="panel-grid">
        <Panel eyebrow="Tools" title="Where the rest lives" flush>
          <table>
            <tbody>
              {data.tools.map((tool) => (
                <tr key={tool.name}>
                  <td>
                    {tool.url ? (
                      <a href={tool.url} target="_blank" rel="noreferrer">
                        {tool.name} ↗
                      </a>
                    ) : (
                      tool.name
                    )}
                    <div className="muted small">{tool.purpose}</div>
                  </td>
                  <td className="muted wrap small">{tool.note ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>

        <Panel eyebrow="Runbooks" title="Still done from a terminal">
          <div className="stack">
            {data.runbooks.map((runbook) => (
              <div key={runbook.title}>
                <div>{runbook.title}</div>
                <div className="muted small">{runbook.when}</div>
                <code className="token" style={{ marginTop: 6, borderColor: 'var(--seam-bright)' }}>
                  {runbook.command}
                </code>
              </div>
            ))}
          </div>
        </Panel>
      </div>
    </>
  );
}
